import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Container } from "../components/Container";
import { FormActions, FormActionsStart } from "../components/FormActions";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageDetailHeader } from "../components/PageDetailHeader";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { CATALOG_NEW_ITEM_ID, useCatalogNav } from "./catalog/catalogNav";
import { PrepStationForm } from "./components/PrepStationForm";
import {
  useGetPrepStationQuery,
  useCreatePrepStationMutation,
  useUpdatePrepStationMutation,
  useDeletePrepStationMutation,
} from "../query/prepStationsQuery";
import type { RestCreatePrepStation } from "@KassaroAB/shared/types/prepStation";

const FORM_ID = "catalog-prep-station-form";

export const PrepStationFormView = () => {
  const { t } = useTranslation();
  const { setDirty, allowNavigate } = useCatalogNav();
  const { scope, pagePath } = useCatalogScope("prepStations");
  const { itemId } = useParams<{ itemId: string }>();
  const isCreating = itemId === CATALOG_NEW_ITEM_ID;
  const prepStationId = !isCreating && itemId ? itemId : null;

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);

  const {
    data: prepStation,
    isLoading,
    error: loadError,
  } = useGetPrepStationQuery(scope, prepStationId);
  const {
    mutate: createMutation,
    error: createError,
    isPending: isCreatePending,
  } = useCreatePrepStationMutation(scope);
  const {
    mutate: updateMutation,
    error: updateError,
    isPending: isUpdatePending,
  } = useUpdatePrepStationMutation(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeletePrepStationMutation(scope);

  const isSaving = isCreatePending || isUpdatePending;
  const missing = !isCreating && !!prepStationId && !isLoading && !loadError && !prepStation;

  const handleSubmit = (data: RestCreatePrepStation) => {
    if (isCreating) {
      createMutation(data, {
        onSuccess: (created) => {
          allowNavigate(`${pagePath}/${created.id}`);
        },
      });
      return;
    }
    if (!prepStationId) return;
    updateMutation(
      { prepStationId, prepStation: data },
      {
        onSuccess: () => {
          setSuccessMessage(t("prepStationsView.successUpdated"));
          setSavedTick((tick) => tick + 1);
        },
        onError: () => setSuccessMessage(null),
      },
    );
  };

  const confirmDelete = () => {
    if (!prepStationId) return;
    deleteMutation(prepStationId, {
      onSuccess: () => {
        setDeleteOpen(false);
        allowNavigate(pagePath);
      },
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const title = isCreating
    ? t("prepStationsView.panelCreate")
    : (prepStation?.name ?? t("prepStationsView.title"));
  const breadcrumbCurrent = isCreating
    ? t("prepStationsView.panelCreate")
    : missing
      ? t("catalog.itemNotFound")
      : (prepStation?.name ?? t("common.loading"));

  if (!scope) {
    return null;
  }

  const showForm = isCreating || (!!prepStation && !missing);

  return (
    <Container>
      <PageDetailHeader
        title={title}
        parentLabel={t("prepStationsView.title")}
        parentTo={pagePath}
        currentLabel={breadcrumbCurrent}
      />
      {loadError && (
        <InfoBox variant="error">{t("prepStationsView.errorLoad")}</InfoBox>
      )}
      {(createError || updateError || deleteError) && (
        <InfoBox variant="error">{t("prepStationsView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      <Card>
        {!isCreating && isLoading && !prepStation ? (
          <LoadingContainer message={t("prepStationsView.loading")} />
        ) : missing ? (
          <InfoBox variant="error">{t("catalog.itemNotFound")}</InfoBox>
        ) : showForm ? (
          <>
            <PrepStationForm
              key={prepStation?.id ?? CATALOG_NEW_ITEM_ID}
              formId={FORM_ID}
              prepStation={isCreating ? undefined : prepStation}
              savedTick={savedTick}
              onSubmit={handleSubmit}
              onDirtyChange={setDirty}
            />
            <FormActions>
              {!isCreating && (
                <FormActionsStart>
                  <Button
                    type="button"
                    $color="danger"
                    onClick={() => setDeleteOpen(true)}
                    disabled={isSaving || isDeletePending}
                  >
                    {t("common.delete")}
                  </Button>
                </FormActionsStart>
              )}
              <RouterButtonLink
                to={pagePath}
                $color="secondary"
                onClick={(event) => {
                  if (isSaving) event.preventDefault();
                }}
              >
                {t("common.cancel")}
              </RouterButtonLink>
              <Button type="submit" form={FORM_ID} disabled={isSaving}>
                {isSaving
                  ? t("common.saving")
                  : isCreating
                    ? t("common.create")
                    : t("common.update")}
              </Button>
            </FormActions>
          </>
        ) : null}
      </Card>
      <ConfirmDialog
        isOpen={deleteOpen}
        title={t("prepStationForm.deletePrepStation")}
        message={t("prepStationsView.deleteConfirm", {
          name: prepStation?.name ?? "",
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Container>
  );
};
